import React from 'react';
import { OptionCell, LTPCell } from './OptionTableCell';
import CallHoverComponent from './CallPutHover/CallHoverComponent';
import PutHoverComponent from './CallPutHover/PutHoverComponent';

/**
 * The three-panel scrollable body of the Option Chain:
 * CE tbody | Strike tbody | PE tbody
 * Also renders hover overlays for Buy/Sell chips.
 */
const OptionChainTableBody = React.forwardRef(function OptionChainTableBody(
  {
    filteredRows,
    dynamicRows,
    isMobile,
    filteredCallHeaders,
    filteredPutHeaders,
    atmIndex,
    spotPrice,
    hoveredRow,
    hoveredSide,
    handleRowMouseEnter,
    handleRowMouseLeave,
    handleRowClick,
    handleLTPClick,
    callTableWidth,
    putTableWidth,
    strikeW,
    ceColWidthPct,
    peColWidthPct,
    ceColgroup,
    peColgroup,
    strikeColgroup,
    atmBadgeRef,
    scrollToATM,
  },
  refs
) {
  const { ceBodyClipRef, peBodyClipRef, ceBodyTableRef, peBodyTableRef } = refs;

  return (
    <div className="oc-body" onMouseLeave={handleRowMouseLeave}>
      {/* CE hover overlay */}
      {hoveredRow !== null && hoveredSide === 'ce' && filteredRows[hoveredRow] && filteredCallHeaders.length > 0 && (
        <div style={{ position: 'absolute', top: hoveredRow * 32, left: 0, width: 220, height: 32, zIndex: 20, pointerEvents: 'none' }}>
          <div
            style={{ pointerEvents: 'auto', width: '100%', height: '100%', position: 'relative' }}
            onMouseEnter={() => handleRowMouseEnter(hoveredRow, 'ce')}
            onMouseLeave={handleRowMouseLeave}
          >
            <CallHoverComponent
              row={{
                ltp: filteredRows[hoveredRow].ce_ltp || 0,
                trading_symbol: filteredRows[hoveredRow].call_option?.trading_symbol || filteredRows[hoveredRow].ce_trading_symbol,
                token: filteredRows[hoveredRow].call_option?.token || filteredRows[hoveredRow].ce_token,
                exchange: filteredRows[hoveredRow].call_option?.exchange || filteredRows[hoveredRow].ce_exchange || 'NSE',
                display_name: filteredRows[hoveredRow].call_option?.display_name || filteredRows[hoveredRow].ce_display_name || filteredRows[hoveredRow].call_option?.trading_symbol,
                tradable: filteredRows[hoveredRow].call_option?.tradable !== false && filteredRows[hoveredRow].ce_tradable !== false
              }}
              isHover={true}
            />
          </div>
        </div>
      )}

      {/* PE hover overlay */}
      {hoveredRow !== null && hoveredSide === 'pe' && filteredRows[hoveredRow] && filteredPutHeaders.length > 0 && (
        <div style={{ position: 'absolute', top: hoveredRow * 32, right: 0, width: 220, height: 32, zIndex: 20, pointerEvents: 'none' }}>
          <div
            style={{ pointerEvents: 'auto', width: '100%', height: '100%', position: 'relative' }}
            onMouseEnter={() => handleRowMouseEnter(hoveredRow, 'pe')}
            onMouseLeave={handleRowMouseLeave}
          >
            <PutHoverComponent
              row={{
                ltp: filteredRows[hoveredRow].pe_ltp || 0,
                trading_symbol: filteredRows[hoveredRow].put_option?.trading_symbol || filteredRows[hoveredRow].pe_trading_symbol,
                token: filteredRows[hoveredRow].put_option?.token || filteredRows[hoveredRow].pe_token,
                exchange: filteredRows[hoveredRow].put_option?.exchange || filteredRows[hoveredRow].pe_exchange || 'NSE',
                display_name: filteredRows[hoveredRow].put_option?.display_name || filteredRows[hoveredRow].pe_display_name || filteredRows[hoveredRow].put_option?.trading_symbol,
                tradable: filteredRows[hoveredRow].put_option?.tradable !== false && filteredRows[hoveredRow].pe_tradable !== false
              }}
              isHover={true}
            />
          </div>
        </div>
      )}

      {/* CE Body Clip */}
      <div ref={ceBodyClipRef} className="oc-ce-clip">
        <table ref={ceBodyTableRef} className="oc-table oc-ce-table" style={{ width: callTableWidth, minWidth: callTableWidth }}>
          {ceColgroup}
          <tbody>
            {filteredRows.map((row, ri) => {
              const originalIndex = dynamicRows.indexOf(row);
              const isAtmRow = originalIndex === atmIndex;
              const isITM = originalIndex <= atmIndex;

              const ceLTP = parseFloat(row.ce_ltp) || 0;
              const ceClose = parseFloat(row.ce_close) || 0;
              const cePercentChange = ceClose !== 0 ? ((ceLTP - ceClose) / ceClose) * 100 : 0;

              return (
                <React.Fragment key={`ce-frag-${row.strike_price}-${ri}`}>
                  <tr
                    key={`ce-${row.strike_price}-${ri}`}
                    className={`oc-row ${hoveredRow === ri && filteredCallHeaders.length > 0 ? 'oc-row--hovered' : ''}`}
                    onMouseEnter={() => handleRowMouseEnter(ri, 'ce')}
                    onMouseLeave={handleRowMouseLeave}
                    onClick={() => handleRowClick && handleRowClick(ri, 'ce')}
                  >
                    {filteredCallHeaders.map((header, i) => {
                      const headerLower = header.toLowerCase();
                      let rowKey = headerLower;
                      if (headerLower === 'oi change%') rowKey = 'oi_change_percent';

                      const value = row[`ce_${rowKey}`];
                      const isLTPColumn = headerLower === 'ltp';
                      const numValue = parseFloat(value);
                      const valueClass = numValue > 0 ? 'value-up' : numValue < 0 ? 'value-down' : '';

                      return (
                        <td
                          key={i}
                          className={`oc-td ${isITM ? 'oc-td--itm' : ''}`}
                          style={{ width: ri === 0 ? ceColWidthPct : undefined, position: 'relative', cursor: 'pointer' }}
                        >
                          {isLTPColumn ? (
                            <LTPCell header={header} value={value} isAtmRow={isAtmRow} valueClass={valueClass} side="ce" onClick={isMobile ? undefined : (e) => handleLTPClick(row, 'ce', e)} percentChange={cePercentChange} asContent={true} />
                          ) : (
                            <OptionCell header={header} value={value} isAtmRow={isAtmRow} valueClass={valueClass} asContent={true} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {isAtmRow && spotPrice && spotPrice > 0 && (
                    <tr className="oc-atm-row">
                      <td colSpan={filteredCallHeaders.length} className="oc-atm-cell" />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Strike Body */}
      <div className="oc-strike-body" style={{ width: strikeW, minWidth: strikeW, position: 'relative', zIndex: 2 }}>
        <table className="oc-table" style={{ width: '100%' }}>
          {strikeColgroup}
          <tbody>
            {filteredRows.map((row, ri) => {
              const originalIndex = dynamicRows.indexOf(row);
              const isAtmRow = originalIndex === atmIndex;

              return (
                <React.Fragment key={`strike-${row.strike_price}-${ri}`}>
                  <tr
                    className={`oc-row ${hoveredRow === ri ? 'oc-row--hovered' : ''}`}
                    onMouseEnter={() => handleRowMouseEnter(ri)}
                    onMouseLeave={handleRowMouseLeave}
                  >
                    <td className="oc-td oc-td--strike">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '0 4px', boxSizing: 'border-box' }}>
                        {row.strike_price}
                      </div>
                    </td>
                  </tr>
                  {isAtmRow && spotPrice && spotPrice > 0 && (
                    <tr className="oc-atm-row">
                      <td className="oc-atm-cell">
                        <div className="oc-atm-line" />
                        <div className="oc-atm-badge" ref={atmBadgeRef} onClick={scrollToATM}>
                          {spotPrice ? spotPrice.toFixed(2) : "0.00"}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PE Body Clip */}
      <div ref={peBodyClipRef} className="oc-pe-clip">
        <table ref={peBodyTableRef} className="oc-table oc-pe-table" style={{ width: putTableWidth, minWidth: putTableWidth }}>
          {peColgroup}
          <tbody>
            {filteredRows.map((row, ri) => {
              const originalIndex = dynamicRows.indexOf(row);
              const isAtmRow = originalIndex === atmIndex;
              const isITM = originalIndex > atmIndex;

              const peLTP = parseFloat(row.pe_ltp) || 0;
              const peClose = parseFloat(row.pe_close) || 0;
              const pePercentChange = peClose !== 0 ? ((peLTP - peClose) / peClose) * 100 : 0;

              return (
                <React.Fragment key={`pe-frag-${row.strike_price}-${ri}`}>
                  <tr
                    key={`pe-${row.strike_price}-${ri}`}
                    className={`oc-row ${hoveredRow === ri && filteredPutHeaders.length > 0 ? 'oc-row--hovered' : ''}`}
                    onMouseEnter={() => handleRowMouseEnter(ri, 'pe')}
                    onMouseLeave={handleRowMouseLeave}
                    onClick={() => handleRowClick && handleRowClick(ri, 'pe')}
                  >
                    {filteredPutHeaders.map((header, i) => {
                      const headerLower = header.toLowerCase();
                      let rowKey = headerLower;
                      if (headerLower === 'oi change%') rowKey = 'oi_change_percent';

                      const value = row[`pe_${rowKey}`];
                      const isLTPColumn = headerLower === 'ltp';
                      const numValue = parseFloat(value);
                      const valueClass = numValue > 0 ? 'value-up' : numValue < 0 ? 'value-down' : '';

                      return (
                        <td
                          key={i}
                          className={`oc-td ${isITM ? 'oc-td--itm' : ''}`}
                          style={{ width: ri === 0 ? peColWidthPct : undefined, position: 'relative', cursor: 'pointer' }}
                        >
                          {isLTPColumn ? (
                            <LTPCell header={header} value={value} isAtmRow={isAtmRow} valueClass={valueClass} side="pe" onClick={isMobile ? undefined : (e) => handleLTPClick(row, 'pe', e)} percentChange={pePercentChange} asContent={true} />
                          ) : (
                            <OptionCell header={header} value={value} isAtmRow={isAtmRow} valueClass={valueClass} asContent={true} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {isAtmRow && spotPrice && spotPrice > 0 && (
                    <tr className="oc-atm-row">
                      <td colSpan={filteredPutHeaders.length} className="oc-atm-cell" />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default OptionChainTableBody;
